import { useEffect, useState, useRef } from 'react';
import { Card, Row, Col, Statistic, Table, DatePicker, Select, Button, Spin, message, Input, Modal, Tag, Descriptions, Popconfirm } from 'antd';
import { 
  EyeOutlined, 
  ThunderboltOutlined, 
  GlobalOutlined, 
  FileTextOutlined,
  ReloadOutlined,
  MobileOutlined,
  DesktopOutlined,
  TabletOutlined,
  UserOutlined,
  SearchOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { apiClient } from '../store/services/auth';
import { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

interface TrackingStats {
  totalEvents: number;
  pageViews: number;
  clicks: number;
  byCountry: Array<{ country: string; count: number }>;
  byPath: Array<{ path: string; count: number }>;
  byDevice: Array<{ deviceType: string; count: number }>;
  byBrowser: Array<{ browserName: string; count: number }>;
  byOS: Array<{ os: string; count: number }>;
  byLocale: Array<{ locale: string; count: number }>;
}

interface User {
  ip: string;
  country: string | null;
  city: string | null;
  deviceType: string | null;
  browserName: string | null;
  os: string | null;
  locale: string | null;
  totalEvents: number;
  lastSeen: string | null;
}

interface UserEvent {
  id: string;
  eventType: string;
  path: string;
  url: string | null;
  elementId: string | null;
  elementType: string | null;
  elementText: string | null;
  referer: string | null;
  createdAt: string;
}

interface UserDetails {
  ip: string;
  userInfo: {
    country: string | null;
    city: string | null;
    deviceType: string | null;
    browserName: string | null;
    browserVersion: string | null;
    os: string | null;
    osVersion: string | null;
    locale: string | null;
    language: string | null;
    timezone: string | null;
    screenWidth: number | null;
    screenHeight: number | null;
    isMobile: boolean | null;
    isTablet: boolean | null;
    isDesktop: boolean | null;
    userAgent: string | null;
  } | null;
  firstSeen: string | null;
  lastSeen: string | null;
  totalEvents: number;
  events: UserEvent[];
}

export function TrackingStatsPage() {
  const [stats, setStats] = useState<TrackingStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [eventTablePagination, setEventTablePagination] = useState({ current: 1, pageSize: 10 });
  const [eventFilters, setEventFilters] = useState<{
    eventType?: string;
    path?: string;
  }>({});
  const [modalFullscreen, setModalFullscreen] = useState(false);
  const [modalSize, setModalSize] = useState({ width: 1000, height: 600 });
  const resizeRef = useRef<{ isResizing: boolean; startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);
  const [filters, setFilters] = useState<{
    startDate?: string;
    endDate?: string;
    country?: string;
    path?: string;
    ip?: string;
  }>({});

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.country) params.append('country', filters.country);
      if (filters.path) params.append('path', filters.path);
      if (filters.ip) params.append('ip', filters.ip);

      const response = await apiClient.get<{ data: TrackingStats }>(
        `/tracking/stats${params.toString() ? `?${params.toString()}` : ''}`
      );
      
      // apiClient retourne { data: {...}, success: true }
      setStats((response as any).data);
    } catch (error: any) {
      console.error('Error fetching tracking stats:', error);
      message.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get<{ data: User[] }>(
        `/tracking/users${params.toString() ? `?${params.toString()}` : ''}`
      );
      
      setUsers((response as any).data || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      message.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchUserDetails = async (ip: string) => {
    setLoading(true);
    // Réinitialiser les filtres et la pagination
    setEventFilters({});
    setEventTablePagination({ current: 1, pageSize: 10 });
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get<{ data: UserDetails }>(
        `/tracking/users/${encodeURIComponent(ip)}/events${params.toString() ? `?${params.toString()}` : ''}`
      );
      
      setSelectedUser((response as any).data);
      setUserModalVisible(true);
    } catch (error: any) {
      console.error('Error fetching user details:', error);
      message.error('Erreur lors du chargement des détails de l\'utilisateur');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setUserModalVisible(false);
    setSelectedUser(null);
    setModalFullscreen(false);
    // Réinitialiser les filtres et la pagination
    setEventFilters({});
    setEventTablePagination({ current: 1, pageSize: 10 });
  };

  // Gestion du redimensionnement à la souris
  useEffect(() => {
    if (modalFullscreen || !userModalVisible) return;

    let resizeHandle: HTMLElement | null = null;
    let modalElement: HTMLElement | null = null;
    let cleanup: (() => void) | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    // Fonction pour initialiser le redimensionnement
    const initResize = () => {
      // Essayer plusieurs sélecteurs possibles
      modalElement = 
        document.querySelector('.user-details-modal:not(.fullscreen-modal) .ant-modal') as HTMLElement ||
        document.querySelector('.ant-modal-wrap .user-details-modal .ant-modal') as HTMLElement ||
        document.querySelector('.ant-modal-wrap:has(.user-details-modal) .ant-modal') as HTMLElement;

      // Si pas trouvé, chercher toutes les modals et filtrer
      if (!modalElement) {
        const allModals = document.querySelectorAll('.ant-modal');
        allModals.forEach((modal) => {
          if (modal.classList.contains('user-details-modal') || 
              modal.closest('.user-details-modal') ||
              modal.querySelector('.user-details-modal')) {
            modalElement = modal as HTMLElement;
          }
        });
      }

      // Dernière tentative : chercher par le contenu
      if (!modalElement) {
        const modals = document.querySelectorAll('.ant-modal');
        modals.forEach((modal) => {
          const title = modal.querySelector('.ant-modal-title');
          if (title && title.textContent?.includes('Détails utilisateur')) {
            modalElement = modal as HTMLElement;
          }
        });
      }

      if (!modalElement) {
        return false; // Pas encore trouvé
      }

      // S'assurer que la modal a position relative pour le handle
      if (getComputedStyle(modalElement).position === 'static') {
        modalElement.style.position = 'relative';
      }

      // S'assurer que la modal a une hauteur définie
      if (!modalElement.style.height || modalElement.style.height === 'auto') {
        modalElement.style.height = `${modalSize.height}px`;
      }

      // Créer le handle de redimensionnement s'il n'existe pas
      resizeHandle = modalElement.querySelector('.resize-handle') as HTMLElement;
      if (!resizeHandle) {
        resizeHandle = document.createElement('div');
        resizeHandle.className = 'resize-handle';
        modalElement.appendChild(resizeHandle);
      }

      const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('resize-handle')) return;

        e.preventDefault();
        e.stopPropagation();
        
        if (!modalElement) return;
        
        resizeRef.current = {
          isResizing: true,
          startX: e.clientX,
          startY: e.clientY,
          startWidth: modalElement.offsetWidth,
          startHeight: modalElement.offsetHeight,
        };
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!resizeRef.current?.isResizing || !modalElement) return;

        const deltaX = e.clientX - resizeRef.current.startX;
        const deltaY = e.clientY - resizeRef.current.startY;

        const newWidth = Math.max(600, resizeRef.current.startWidth + deltaX);
        const newHeight = Math.max(400, resizeRef.current.startHeight + deltaY);

        modalElement.style.width = `${newWidth}px`;
        modalElement.style.height = `${newHeight}px`;
        setModalSize({ width: newWidth, height: newHeight });
      };

      const handleMouseUp = () => {
        if (resizeRef.current) {
          resizeRef.current.isResizing = false;
        }
      };

      resizeHandle.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      cleanup = () => {
        resizeHandle?.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Nettoyer le handle si nécessaire
        if (resizeHandle && resizeHandle.parentNode) {
          resizeHandle.parentNode.removeChild(resizeHandle);
        }
      };

      return true; // Succès
    };

    // Essayer immédiatement
    if (initResize()) {
      return () => {
        if (cleanup) {
          cleanup();
        }
      };
    }

    // Si pas trouvé, essayer avec un intervalle
    let attempts = 0;
    const maxAttempts = 20; // 2 secondes max (20 * 100ms)
    
    intervalId = setInterval(() => {
      attempts++;
      if (initResize() || attempts >= maxAttempts) {
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    }, 100);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [modalFullscreen, userModalVisible, modalSize.height]);

  // Filtrer les événements selon les filtres
  const getFilteredEvents = (): UserEvent[] => {
    if (!selectedUser) return [];
    
    let filtered = [...selectedUser.events];
    
    if (eventFilters.eventType) {
      filtered = filtered.filter(e => e.eventType === eventFilters.eventType);
    }
    
    if (eventFilters.path) {
      filtered = filtered.filter(e => e.path === eventFilters.path);
    }
    
    return filtered;
  };

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, []);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({
        ...filters,
        startDate: dates[0].startOf('day').toISOString(),
        endDate: dates[1].endOf('day').toISOString(),
      });
    } else {
      setFilters({
        ...filters,
        startDate: undefined,
        endDate: undefined,
      });
    }
  };

  const handleCountryChange = (country: string) => {
    setFilters({
      ...filters,
      country: country === 'all' ? undefined : country,
    });
  };

  const handlePathChange = (path: string) => {
    setFilters({
      ...filters,
      path: path === 'all' ? undefined : path,
    });
  };

  const handleIpChange = (ip: string) => {
    setFilters({
      ...filters,
      ip: ip === '' ? undefined : ip,
    });
  };

  const handleApplyFilters = () => {
    fetchStats();
    fetchUsers();
  };

  const handleResetFilters = () => {
    setFilters({});
    setTimeout(() => {
      fetchStats();
      fetchUsers();
    }, 100);
  };

  const handleSeedDemo = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post<{ data: { success: boolean; message: string; numUsers: number; totalEvents: number } }>(
        '/tracking/seed-demo?numUsers=10&eventsPerUser=15'
      );
      
      const result = (response as any).data;
      if (result.success) {
        message.success(`✅ ${result.totalEvents} événements générés pour ${result.numUsers} utilisateurs !`);
        // Rafraîchir les données
        setTimeout(() => {
          fetchStats();
          fetchUsers();
        }, 1000);
      } else {
        message.error(result.message || 'Erreur lors de la génération des données');
      }
    } catch (error: any) {
      console.error('Error seeding demo data:', error);
      message.error('Erreur lors de la génération des données de démonstration');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    setLoading(true);
    try {
      const response = await apiClient.delete<{ data: { success: boolean; message: string; deletedCount: number } }>(
        '/tracking/clear'
      );
      
      const result = (response as any).data;
      if (result.success) {
        message.success(`✅ ${result.deletedCount} événements supprimés !`);
        // Rafraîchir les données
        setTimeout(() => {
          fetchStats();
          fetchUsers();
        }, 500);
      } else {
        message.error(result.message || 'Erreur lors de la suppression des données');
      }
    } catch (error: any) {
      console.error('Error clearing tracking data:', error);
      message.error('Erreur lors de la suppression des données');
    } finally {
      setLoading(false);
    }
  };

  const countryColumns = [
    {
      title: 'Pays',
      dataIndex: 'country',
      key: 'country',
      render: (country: string) => country === 'Unknown' ? 'Non défini' : country,
    },
    {
      title: 'Visites',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
    },
  ];

  const pathColumns = [
    {
      title: 'Page',
      dataIndex: 'path',
      key: 'path',
    },
    {
      title: 'Visites',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
    },
  ];

  const deviceColumns = [
    {
      title: 'Type de device',
      dataIndex: 'deviceType',
      key: 'deviceType',
      render: (deviceType: string) => {
        const icons: Record<string, any> = {
          mobile: <MobileOutlined />,
          tablet: <TabletOutlined />,
          desktop: <DesktopOutlined />,
        };
        return (
          <span>
            {icons[deviceType] || <FileTextOutlined />} {deviceType || 'Unknown'}
          </span>
        );
      },
    },
    {
      title: 'Visites',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
    },
  ];

  const browserColumns = [
    {
      title: 'Navigateur',
      dataIndex: 'browserName',
      key: 'browserName',
    },
    {
      title: 'Visites',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
    },
  ];

  const osColumns = [
    {
      title: 'Système d\'exploitation',
      dataIndex: 'os',
      key: 'os',
    },
    {
      title: 'Visites',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
    },
  ];

  const localeColumns = [
    {
      title: 'Locale',
      dataIndex: 'locale',
      key: 'locale',
    },
    {
      title: 'Visites',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
    },
  ];

  const uniqueCountries = stats?.byCountry.map(c => c.country) || [];
  const uniquePaths = stats?.byPath.map(p => p.path) || [];

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Data Tracking</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Analyse des visites et interactions sur la landing page</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            type="dashed"
            onClick={handleSeedDemo}
            loading={loading}
            icon={<FileTextOutlined />}
            className="w-full sm:w-auto"
            block
          >
            <span className="hidden sm:inline">Générer données demo</span>
            <span className="sm:hidden">Générer demo</span>
          </Button>
          <Popconfirm
            title="Supprimer toutes les données de tracking ?"
            description="Cette action est irréversible. Toutes les données de tracking seront supprimées."
            onConfirm={handleClearAll}
            okText="Oui, supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              loading={loading}
              icon={<DeleteOutlined />}
              className="w-full sm:w-auto"
              block
            >
              <span className="hidden sm:inline">Vider les données</span>
              <span className="sm:hidden">Vider</span>
            </Button>
          </Popconfirm>
        </div>
      </div>

      {/* Filtres */}
      <Card className="mb-6">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={6}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateRangeChange}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
            <Select
              style={{ width: '100%' }}
              placeholder="Tous les pays"
              allowClear
              onChange={handleCountryChange}
            >
              <Select.Option value="all">Tous les pays</Select.Option>
              {uniqueCountries.map(country => (
                <Select.Option key={country} value={country}>
                  {country === 'Unknown' ? 'Non défini' : country}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Page</label>
            <Select
              style={{ width: '100%' }}
              placeholder="Toutes les pages"
              allowClear
              onChange={handlePathChange}
            >
              <Select.Option value="all">Toutes les pages</Select.Option>
              {uniquePaths.map(path => (
                <Select.Option key={path} value={path}>
                  {path}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label className="block text-sm font-medium text-gray-700 mb-2">IP</label>
            <Input
              placeholder="Filtrer par IP"
              prefix={<SearchOutlined />}
              onChange={(e) => handleIpChange(e.target.value)}
              value={filters.ip || ''}
            />
          </Col>
        </Row>
        <Row gutter={16} className="mt-4">
          <Col xs={24}>
            <div className="flex gap-2">
              <Button 
                type="primary" 
                onClick={handleApplyFilters}
                loading={loading || usersLoading}
              >
                Appliquer
              </Button>
              <Button onClick={handleResetFilters}>
                Réinitialiser
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {loading && !stats ? (
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Statistiques principales */}
          <Row gutter={16} className="mb-6">
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Total d'événements"
                  value={stats?.totalEvents || 0}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Vues de page"
                  value={stats?.pageViews || 0}
                  prefix={<EyeOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Clics"
                  value={stats?.clicks || 0}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Tableaux détaillés */}
          <Row gutter={16}>
            <Col xs={24} lg={12} className="mb-4">
              <Card
                title={
                  <span>
                    <GlobalOutlined className="mr-2" />
                    Visites par pays
                  </span>
                }
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={fetchStats}
                    loading={loading}
                  />
                }
              >
                <Table
                  dataSource={stats?.byCountry || []}
                  columns={countryColumns}
                  rowKey="country"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12} className="mb-4">
              <Card
                title={
                  <span>
                    <FileTextOutlined className="mr-2" />
                    Visites par page
                  </span>
                }
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={fetchStats}
                    loading={loading}
                  />
                }
              >
                <Table
                  dataSource={stats?.byPath || []}
                  columns={pathColumns}
                  rowKey="path"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12} className="mb-4">
              <Card
                title={
                  <span>
                    <MobileOutlined className="mr-2" />
                    Visites par device
                  </span>
                }
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={fetchStats}
                    loading={loading}
                  />
                }
              >
                <Table
                  dataSource={stats?.byDevice || []}
                  columns={deviceColumns}
                  rowKey="deviceType"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12} className="mb-4">
              <Card
                title={
                  <span>
                    <DesktopOutlined className="mr-2" />
                    Navigateurs
                  </span>
                }
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={fetchStats}
                    loading={loading}
                  />
                }
              >
                <Table
                  dataSource={stats?.byBrowser || []}
                  columns={browserColumns}
                  rowKey="browserName"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12} className="mb-4">
              <Card
                title={
                  <span>
                    <FileTextOutlined className="mr-2" />
                    Systèmes d'exploitation
                  </span>
                }
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={fetchStats}
                    loading={loading}
                  />
                }
              >
                <Table
                  dataSource={stats?.byOS || []}
                  columns={osColumns}
                  rowKey="os"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12} className="mb-4">
              <Card
                title={
                  <span>
                    <GlobalOutlined className="mr-2" />
                    Langues
                  </span>
                }
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={fetchStats}
                    loading={loading}
                  />
                }
              >
                <Table
                  dataSource={stats?.byLocale || []}
                  columns={localeColumns}
                  rowKey="locale"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
          </Row>

          {/* Liste des utilisateurs */}
          <Card
            className="mt-6"
            title={
              <span className="text-base sm:text-lg">
                <UserOutlined className="mr-2" />
                Utilisateurs uniques ({users.length})
              </span>
            }
            extra={
              <Button
                icon={<ReloadOutlined />}
                size="small"
                onClick={fetchUsers}
                loading={usersLoading}
              />
            }
          >
            <div className="overflow-x-auto">
              <Table
                dataSource={users}
                columns={[
                  {
                    title: 'IP',
                    dataIndex: 'ip',
                    key: 'ip',
                    width: 150,
                    render: (ip: string) => (
                      <Button
                        type="link"
                        onClick={() => fetchUserDetails(ip)}
                        style={{ padding: 0 }}
                        className="text-xs sm:text-sm"
                      >
                        {ip}
                      </Button>
                    ),
                  },
                  {
                    title: 'Pays',
                    dataIndex: 'country',
                    key: 'country',
                    width: 120,
                    render: (country: string | null) => (
                      <span className="text-xs sm:text-sm">{country || 'Non défini'}</span>
                    ),
                  },
                  {
                    title: 'Device',
                    dataIndex: 'deviceType',
                    key: 'deviceType',
                    width: 120,
                    render: (deviceType: string | null) => {
                      const icons: Record<string, any> = {
                        mobile: <MobileOutlined />,
                        tablet: <TabletOutlined />,
                        desktop: <DesktopOutlined />,
                      };
                      return (
                        <span className="text-xs sm:text-sm">
                          {icons[deviceType || ''] || <FileTextOutlined />} {deviceType || 'Unknown'}
                        </span>
                      );
                    },
                  },
                  {
                    title: 'Navigateur',
                    dataIndex: 'browserName',
                    key: 'browserName',
                    width: 150,
                    render: (browser: string | null) => (
                      <span className="text-xs sm:text-sm">{browser || 'Unknown'}</span>
                    ),
                  },
                  {
                    title: 'OS',
                    dataIndex: 'os',
                    key: 'os',
                    width: 120,
                    render: (os: string | null) => (
                      <span className="text-xs sm:text-sm">{os || 'Unknown'}</span>
                    ),
                  },
                  {
                    title: 'Événements',
                    dataIndex: 'totalEvents',
                    key: 'totalEvents',
                    width: 100,
                    sorter: (a: any, b: any) => a.totalEvents - b.totalEvents,
                    defaultSortOrder: 'descend' as const,
                    render: (count: number) => (
                      <span className="text-xs sm:text-sm">{count}</span>
                    ),
                  },
                  {
                    title: 'Dernière visite',
                    dataIndex: 'lastSeen',
                    key: 'lastSeen',
                    width: 180,
                    render: (date: string | null) => (
                      <span className="text-xs sm:text-sm">
                        {date ? new Date(date).toLocaleString('fr-FR') : 'N/A'}
                      </span>
                    ),
                    sorter: (a: any, b: any) => 
                      new Date(a.lastSeen || 0).getTime() - new Date(b.lastSeen || 0).getTime(),
                  },
                ]}
                rowKey="ip"
                pagination={{ pageSize: 20 }}
                loading={usersLoading}
                size="small"
                scroll={{ x: 'max-content' }}
              />
            </div>
          </Card>
        </>
      )}

      {/* Modal de détails utilisateur */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <UserOutlined />
            <span className="text-sm sm:text-base">
              <span className="hidden sm:inline">Détails utilisateur - </span>
              <span className="break-all">{selectedUser?.ip || ''}</span>
            </span>
          </div>
        }
        open={userModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width="95vw"
        style={{ maxWidth: '1200px' }}
        className="user-details-modal"
      >
        {selectedUser && (
          <div>
            {/* Informations utilisateur */}
            <Card 
              className="mb-4" 
              title={
                <span className="flex items-center gap-2 text-sm sm:text-base">
                  <GlobalOutlined />
                  Informations utilisateur
                </span>
              }
              size="small"
            >
              <Descriptions 
                bordered 
                column={{ xs: 1, sm: 2 }} 
                size="small"
              >
                <Descriptions.Item label="IP">
                  <Tag color="blue">{selectedUser.ip}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Pays">
                  {selectedUser.userInfo?.country ? (
                    <Tag color="geekblue">{selectedUser.userInfo.country}</Tag>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Ville">{selectedUser.userInfo?.city || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Device">
                  {selectedUser.userInfo?.deviceType ? (
                    <Tag color={selectedUser.userInfo.deviceType === 'mobile' ? 'cyan' : selectedUser.userInfo.deviceType === 'tablet' ? 'purple' : 'orange'}>
                      {selectedUser.userInfo.deviceType}
                    </Tag>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Navigateur">
                  {selectedUser.userInfo?.browserName ? (
                    <span>
                      <Tag>{selectedUser.userInfo.browserName}</Tag>
                      {selectedUser.userInfo.browserVersion && (
                        <span className="text-gray-500 ml-1">v{selectedUser.userInfo.browserVersion}</span>
                      )}
                    </span>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="OS">
                  {selectedUser.userInfo?.os ? (
                    <span>
                      <Tag>{selectedUser.userInfo.os}</Tag>
                      {selectedUser.userInfo.osVersion && (
                        <span className="text-gray-500 ml-1">v{selectedUser.userInfo.osVersion}</span>
                      )}
                    </span>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Locale">{selectedUser.userInfo?.locale || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Langue">{selectedUser.userInfo?.language || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Fuseau horaire">{selectedUser.userInfo?.timezone || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Résolution">
                  {selectedUser.userInfo?.screenWidth && selectedUser.userInfo?.screenHeight
                    ? `${selectedUser.userInfo.screenWidth}x${selectedUser.userInfo.screenHeight}`
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Première visite">
                  {selectedUser.firstSeen ? (
                    <Tag color="green">{new Date(selectedUser.firstSeen).toLocaleString('fr-FR')}</Tag>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Dernière visite">
                  {selectedUser.lastSeen ? (
                    <Tag color="orange">{new Date(selectedUser.lastSeen).toLocaleString('fr-FR')}</Tag>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Total événements" span={2}>
                  <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                    {selectedUser.totalEvents} événements
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Filtres et tableau des événements */}
            <Card
              title={
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm sm:text-base">
                    <FileTextOutlined />
                    Événements ({getFilteredEvents().length} / {selectedUser.events.length})
                  </span>
                </div>
              }
              size="small"
              extra={
                <div className="flex flex-col sm:flex-row gap-2 mt-2 sm:mt-0 w-full sm:w-auto">
                  <Select
                    placeholder="Type d'événement"
                    allowClear
                    className="w-full sm:w-[150px]"
                    value={eventFilters.eventType}
                    onChange={(value) => setEventFilters({ ...eventFilters, eventType: value || undefined })}
                  >
                    <Select.Option value="PAGE_VIEW">Page View</Select.Option>
                    <Select.Option value="CLICK">Click</Select.Option>
                    <Select.Option value="SCROLL">Scroll</Select.Option>
                  </Select>
                  <Select
                    placeholder="Page"
                    allowClear
                    className="w-full sm:w-[200px]"
                    value={eventFilters.path}
                    onChange={(value) => setEventFilters({ ...eventFilters, path: value || undefined })}
                  >
                    {Array.from(new Set(selectedUser.events.map(e => e.path))).map(path => (
                      <Select.Option key={path} value={path}>{path}</Select.Option>
                    ))}
                  </Select>
                  {(eventFilters.eventType || eventFilters.path) && (
                    <Button
                      size="small"
                      onClick={() => setEventFilters({})}
                      className="w-full sm:w-auto"
                      block
                    >
                      Réinitialiser
                    </Button>
                  )}
                </div>
              }
            >
              <div className="overflow-x-auto">
                <Table
                  dataSource={getFilteredEvents()}
                  columns={[
                    {
                      title: 'Type',
                      dataIndex: 'eventType',
                      key: 'eventType',
                      width: 100,
                      sorter: (a: UserEvent, b: UserEvent) => a.eventType.localeCompare(b.eventType),
                      render: (type: string) => (
                        <Tag color={type === 'PAGE_VIEW' ? 'blue' : type === 'CLICK' ? 'green' : 'orange'}>
                          <span className="text-xs sm:text-sm">
                            {type === 'PAGE_VIEW' ? 'Vue' : type === 'CLICK' ? 'Clic' : type}
                          </span>
                        </Tag>
                      ),
                      filters: [
                        { text: 'PAGE_VIEW', value: 'PAGE_VIEW' },
                        { text: 'CLICK', value: 'CLICK' },
                        { text: 'SCROLL', value: 'SCROLL' },
                      ],
                      onFilter: (value, record: UserEvent) => record.eventType === value,
                    },
                    {
                      title: 'Page',
                      dataIndex: 'path',
                      key: 'path',
                      width: 150,
                      sorter: (a: UserEvent, b: UserEvent) => a.path.localeCompare(b.path),
                      render: (path: string) => (
                        <span className="text-xs sm:text-sm break-all">{path}</span>
                      ),
                      filters: Array.from(new Set(selectedUser.events.map(e => e.path))).map(path => ({
                        text: path,
                        value: path,
                      })),
                      onFilter: (value, record: UserEvent) => record.path === value,
                    },
                    {
                      title: 'Élément',
                      key: 'element',
                      width: 200,
                      render: (_, record: UserEvent) => (
                        <div className="flex flex-col gap-1">
                          {record.elementType && (
                            <Tag className="text-xs">{record.elementType}</Tag>
                          )}
                          {record.elementId && (
                            <span className="text-xs text-gray-500">ID: {record.elementId}</span>
                          )}
                          {record.elementText && (
                            <span className="text-xs sm:text-sm text-gray-700 truncate" title={record.elementText}>
                              {record.elementText.length > 30 ? `${record.elementText.substring(0, 30)}...` : record.elementText}
                            </span>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: 'Referer',
                      dataIndex: 'referer',
                      key: 'referer',
                      width: 150,
                      render: (referer: string | null) => 
                        referer ? (
                          <a 
                            href={referer} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-blue-500 hover:underline truncate block text-xs sm:text-sm"
                          >
                            {referer.length > 25 ? `${referer.substring(0, 25)}...` : referer}
                          </a>
                        ) : (
                          <span className="text-gray-400 text-xs sm:text-sm">Direct</span>
                        ),
                    },
                    {
                      title: 'Date',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      width: 150,
                      sorter: (a: UserEvent, b: UserEvent) => 
                        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                      defaultSortOrder: 'descend' as const,
                      render: (date: string) => (
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm">{new Date(date).toLocaleDateString('fr-FR')}</span>
                          <span className="text-xs text-gray-500">{new Date(date).toLocaleTimeString('fr-FR')}</span>
                        </div>
                      ),
                    },
                  ]}
                  rowKey="id"
                  pagination={{
                    current: eventTablePagination.current,
                    pageSize: eventTablePagination.pageSize,
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} événements`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onChange: (page, pageSize) => {
                      setEventTablePagination({ current: page, pageSize });
                    },
                    onShowSizeChange: (_current, size) => {
                      setEventTablePagination({ current: 1, pageSize: size });
                    },
                    responsive: true,
                  }}
                  size="small"
                  scroll={{ x: 'max-content', y: 400 }}
                />
              </div>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
}

